import type { ReactNode } from 'react';
import { Menu } from 'lucide-react';

export interface TopBarProps {
  title: string;
  breadcrumb?: ReactNode;
  /** Static placeholders this milestone; wired to data in M04+. */
  siteSelector?: ReactNode;
  dateRange?: ReactNode;
  live?: boolean;
  userName?: string;
  /** Opens the mobile nav drawer. Rendered as a hamburger button below `lg`. */
  onMenuClick?: () => void;
}

/**
 * 66px top bar (CLAUDE.md §2 layout): title + breadcrumb + site-selector +
 * date-range + live badge + user chip. Static placeholders only this milestone.
 * Below `lg` a hamburger toggles the off-canvas Sidebar drawer (responsive + PWA, §11).
 */
export function TopBar({
  title,
  breadcrumb,
  siteSelector,
  dateRange,
  live = true,
  userName = 'Signed in',
  onMenuClick,
}: TopBarProps) {
  return (
    <header
      className="sticky top-0 z-10 flex h-topbar items-center gap-4 border-b border-border bg-surface px-4 sm:px-6"
      data-testid="topbar"
    >
      {onMenuClick ? (
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Open menu"
          className="-ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-app-bg hover:text-text lg:hidden"
        >
          <Menu size={20} strokeWidth={2} aria-hidden />
        </button>
      ) : null}

      <div className="min-w-0">
        <h1 className="truncate text-base font-semibold text-text">{title}</h1>
        {breadcrumb ? <div className="text-xs text-muted">{breadcrumb}</div> : null}
      </div>

      <div className="ml-auto flex items-center gap-3">
        <div data-testid="site-selector" className="hidden sm:block">
          {siteSelector ?? (
            <span className="rounded-pill border border-border px-3 py-1 text-sm text-muted">
              South Deep
            </span>
          )}
        </div>
        <div data-testid="date-range" className="hidden md:block">
          {dateRange ?? (
            <span className="rounded-pill border border-border px-3 py-1 text-sm text-muted">
              Last 24h
            </span>
          )}
        </div>
        <span
          data-testid="live-badge"
          className={[
            'inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-xs font-medium',
            live ? 'bg-green/10 text-green' : 'bg-faint/15 text-muted',
          ].join(' ')}
        >
          <span
            className={['h-1.5 w-1.5 rounded-full', live ? 'bg-green' : 'bg-faint'].join(' ')}
          />
          {live ? 'Live' : 'Offline'}
        </span>
        <span
          data-testid="user-chip"
          className="flex items-center gap-2 rounded-pill bg-app-bg px-3 py-1 text-sm font-medium text-text"
        >
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[11px] font-semibold text-white">
            {userName.charAt(0).toUpperCase()}
          </span>
          {userName}
        </span>
      </div>
    </header>
  );
}
